import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Badge, type BadgeVariant } from '@/interfaces/learner/ui';

interface HeroBadge {
  key: string;
  label: ReactNode;
  variant?: BadgeVariant;
}

interface BackLinkConfig {
  to: string;
  label: string;
}

interface CourseHeroHeaderProps {
  eyebrow: string;
  title: string;
  badges?: HeroBadge[];
  summary?: string;
  description?: string | null;
  tags?: ReactNode[];
  backLink?: BackLinkConfig;
  dividerBeforeMeta?: boolean;
}

export default function CourseHeroHeader({
  eyebrow,
  title,
  badges = [],
  summary,
  description,
  tags = [],
  backLink,
  dividerBeforeMeta = false,
}: CourseHeroHeaderProps) {
  return (
    <>
      {backLink && (
        <Link
          to={backLink.to}
          className="text-meta-muted inline-flex items-center gap-1.5 transition-colors"
        >
          <ChevronLeft size={16} />
          {backLink.label}
        </Link>
      )}

      <p
        className={backLink ? 'text-eyebrow mt-6' : 'text-eyebrow'}
        style={{
          color: 'var(--color-accent)',
          marginBottom: 10,
        }}
      >
        {eyebrow}
      </p>

      <h1 className="text-hero-title" style={{ maxWidth: 'var(--measure-hero)' }}>
        {title}
      </h1>

      <div
        className={dividerBeforeMeta ? 'mt-5 pt-4' : undefined}
        style={
          dividerBeforeMeta ? { borderTop: '1px solid var(--color-surface-border)' } : undefined
        }
      >
        {badges.length > 0 && (
          <div
            className={dividerBeforeMeta ? undefined : 'mt-5 flex flex-wrap items-start gap-2.5'}
          >
            <div className="flex flex-wrap items-start gap-2.5">
              {badges.map((badge) => (
                <Badge key={badge.key} variant={badge.variant ?? 'surface'}>
                  {badge.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {summary && (
          <p className="text-body mt-4" style={{ maxWidth: 'var(--measure-copy)' }}>
            {summary}
          </p>
        )}

        {description && (
          <p
            className={`${summary ? 'mt-2' : 'mt-4'} text-body-muted`}
            style={{ maxWidth: 'var(--measure-copy)' }}
          >
            {description}
          </p>
        )}

        {tags.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{tags}</div>}
      </div>
    </>
  );
}
